import DOMPurify from "dompurify"
import { useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { HelpCircle, Search, Users, Trophy, Calendar, Shield, Store } from "lucide-react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import Layout from "@/components/Layout/Layout"

const faqCategories = [
  {
    icon: Users,
    title: "Jugadores",
    description: "Preguntas sobre participación en torneos",
    questions: [
      {
        question: "¿Cómo me registro en un torneo?",
        answer:
          "Para registrarte en un torneo, primero debes tener una cuenta en el sistema. Luego, debes entregar tu código de jugador al organizador del evento y proporcionar información adicional como tu nombre, RUT, y el mazo/raza que usarás en el torneo. Con esto te registrarán en el torneo.",
      },
      {
        question: "¿Puedo modificar mi registro después de inscribirme?",
        answer:
          "Sí, puedes solicitar al organizador del evento modificar tu información de registro hasta que el torneo comience. Una vez que el torneo esté en curso, no podrás cambiar tu mazo o raza registrada. Contacta al organizador si necesitas hacer cambios urgentes.",
      },
      {
        question: "¿Cómo funciona el sistema de ranking?",
        answer:
          "El sistema de ranking se calcula basándose en tus resultados en torneos oficiales. Cada victoria, empate y derrota suma puntos según el tipo de torneo. Los puntos se acumulan por temporada y puedes ver tu posición en el ranking general filtrado por juego específico.",
      },
      {
        question: "¿Qué información puedo ver en mi perfil?",
        answer:
          "En tu perfil puedes ver tus datos personales y un historial de tus últimos torneos. También puedes editar tu información personal, avatar y preferencias.",
      },
    ],
  },
  {
    icon: Trophy,
    title: "Torneos",
    description: "Funcionamiento y tipos de torneos",
    questions: [
      {
        question: "¿Qué tipos de torneos existen?",
        answer:
          "Existen varios tipos de torneos: Competitivos (oficiales con ranking), Casuales (sin ranking), Premier (grandes eventos). Cada tipo tiene diferentes reglas de puntuación y requerimientos. Los torneos competitivos suelen ser parte de temporadas oficiales asignando puntos de ranking.",
      },
      {
        question: "¿Qué es un formato de torneo?",
        answer:
          "El formato define las reglas del juego y qué cartas/ediciones están permitidos. Por ejemplo, en Mitos y Leyendas pueden ser Racial, Racial Libre, Formato Libre, VCR, Imperio Racial, etc. Cada formato tiene su propio meta-juego y lista de cartas permitidas.",
      },
      {
        question: "¿Cómo funcionan las rondas Swiss?",
        answer:
          "En el sistema Swiss, todos los jugadores juegan el mismo número de rondas (generalmente basado en el total de participantes). Después de cada ronda, el sistema empareja jugadores con puntajes similares. No hay eliminación directa hasta una posible ronda final (Top 8, Top 4, etc.).",
      },
      {
        question: "¿Qué es el sistema de Top Cut?",
        answer:
          "El Top Cut es la fase final del torneo donde los mejores jugadores (Top 8, Top 4, etc.) compiten en eliminación directa. La cantidad de jugadores en el Top depende del número total de participantes. Los emparejamientos se basan en el ranking de la fase Swiss.",
      },
    ],
  },
  {
    icon: Shield,
    title: "Organizadores",
    description: "Gestión y administración de eventos",
    questions: [
      {
        question: "¿Cómo creo un torneo?",
        answer:
          "Si eres organizador autorizado, ve a 'Crear Torneo' en la página <b>https://torneos.myl.cl/tournaments</b>. Deberás completar información como: nombre del torneo, descripción, fecha, formato, tipo de torneo, modalidad de juego. El torneo estará visible en la plataforma una vez lo publiques.",
      },
      {
        question: "¿Cómo inicio las rondas del torneo?",
        answer:
          "Una vez que todos los jugadores estén registrados y confirmes la lista, puedes iniciar la primera Ronda desde la sección 'Rondas y Resultados'. El sistema generará automáticamente los emparejamientos. Para rondas siguientes, todos los resultados de la ronda anterior deben estar registrados.",
      },
      {
        question: "¿Cómo registro los resultados de las partidas?",
        answer:
          "En la vista de rondas, cada partida tiene un botón para reportar resultados. Selecciona el ganador, los resultados por juego (2-0, 2-1, etc.), y confirma. Como sugerencia, es útil que ambos jugadores verifiquen y acuerden el resultado antes de reportarlo para evitar errores.",
      },
      {
        question: "¿Puedo cancelar un torneo después de crearlo?",
        answer:
          "Sí, puedes cancelar un torneo desde la sección de Gestión Admin. Sin embargo, si ya hay jugadores registrados o el torneo ha comenzado, la cancelación generará notificaciones a todos los participantes. Asegúrate de comunicar la razón de la cancelación para mantener una buena relación con la comunidad.",
      },
      {
        question: "¿Cómo gestiono los jugadores inscritos?",
        answer:
          "En 'Gestión de Jugadores' puedes ver todos los inscritos, verificar su información, agregar jugadores manualmente, eliminar inscripciones, ajustar las razas de los jugadores, incluso también puedes reemplazar jugadores en caso de inscripciones incorrectas. También puedes exportar la lista de inscritos para tener un registro offline.",
      },
      {
        question: "¿Cómo asigno premios a los ganadores?",
        answer:
          "Después de finalizar el torneo y determinar los ganadores, ve a la sección de 'Posiciones', dentro del menú de 'Rondas y Resultados'. Ahí puedes asignar premios a los jugadores según su posición final. Los premios los defines tu, pero las posiciones del standing final, te ayudará a asignarlos correctamente en base a sus resultados.",
      },
      {
        question: "¿Si un jugador no se presenta, cómo lo registro?",
        answer:
          "Si un jugador no se presenta a una partida, puedes registrar un 'Drop' en la sección de resultados. Esto otorgará la victoria automática al oponente. Asegúrate de comunicarte con el jugador ausente para entender la razón y evitar futuras ausencias.",
      },
    ],
  },
  {
    icon: Store,
    title: "Tiendas",
    description: "Información sobre tiendas asociadas",
    questions: [
      {
        question: "¿Cómo registro mi tienda en el sistema?",
        answer:
          "Para registrar una tienda, necesitas permisos de organizador. Escríbele a nuestro equipo de Juego Organizado en el email jo@klu.cl para asesorarte con la creación y activación de tu cuenta. Se te enviará un link en donde deberás completar la información: nombre comercial, dirección, región, ciudad, teléfono, email, y logo. Las tiendas deben ser aprobadas por un administrador antes de poder crear torneos.",
      },
      {
        question: "¿Puedo ver los torneos de una tienda específica?",
        answer:
          "Sí, cada tienda tiene su propia página de perfil donde puedes ver todos sus torneos pasados, presentes y futuros.",
      },
      {
        question: "¿Cómo edito la información de mi tienda?",
        answer:
          "Si eres el organizador responsable de la tienda, puedes editar su información desde la página de la tienda haciendo clic en 'Editar'. Puedes cambiar horarios, contacto, logo, y otra información relevante. Los cambios se reflejan inmediatamente en el sistema.",
      },
    ],
  },
  {
    icon: Calendar,
    title: "Temporadas",
    description: "Sistema de temporadas y ciclos competitivos",
    questions: [
      {
        question: "¿Qué es una temporada?",
        answer:
          "Una temporada es un período de tiempo (generalmente 6-8 meses) en el cual se acumulan puntos de ranking. Al final de cada temporada, se hace un corte en este ranking en donde los mejores puntuados clasifican al Torneo Nacional del juego al que pertenece el ranking. Las temporadas permiten que el ranking se reinicie periódicamente, dando oportunidades frescas a todos los jugadores. Los puntos del ranking se reinician al inicio de cada nueva temporada y solo se pueden acumular puntos durante la temporada activa, además que el medio para obtener puntos es participando en torneos oficiales durante esa temporada.",
      },
      {
        question: "¿Cómo se calculan los puntos de temporada?",
        answer:
          "Los puntos se otorgan según tu desempeño en torneos oficiales de la temporada. Victorias, posición final, y tipo de torneo afectan los puntos. Torneos más grandes y competitivos otorgan más puntos. El cálculo específico depende del juego y formato, ya que algunos torneos tienen menos puntaje asignado al ranking que otros.",
      },
      {
        question: "¿Puedo ver rankings de temporadas anteriores?",
        answer:
          "Sí, en la sección de Ranking puedes filtrar por temporada específica para ver los resultados históricos. Esto te permite comparar tu progreso entre temporadas y ver quiénes fueron los mejores jugadores anteriores.",
      },
    ],
  },
  //  {
  //    icon: BarChart,
  //    title: "Reportes y Estadísticas",
  //    description: "Análisis de datos y métricas",
  //    questions: [
  //      {
  //        question: "¿Qué es el Reporte de Razas?",
  //        answer:
  //          "El Reporte de Razas muestra estadísticas sobre qué mazos/razas se usaron en un torneo, su tasa de victorias, y su desempeño en diferentes rondas. Es útil para entender el meta-juego y qué estrategias están funcionando mejor.",
  //      },
  //      {
  //        question: "¿Puedo exportar los resultados de un torneo?",
  //        answer:
  //          "Sí, solo los organizadores pueden imprimir o exportar listas de jugadores, emparejamientos, y resultados finales. Esto es útil para mantener registros físicos o compartir información con la comunidad. Y transparentar también a los jugadores, para que puedan ver los resultados oficiales.",
  //      },
  //    ],
  //  },
  {
    icon: HelpCircle,
    title: "Cuenta y Seguridad",
    description: "Gestión de cuenta y privacidad",
    questions: [
      {
        question: "¿Olvidé mi contraseña, qué hago?",
        answer:
          "En la página de inicio de sesión, haz clic en '¿Olvidaste tu contraseña?'. Ingresa tu email registrado o código de jugador y recibirás un enlace para restablecer tu contraseña. El enlace es válido por 24 horas.",
      },
      {
        question: "¿Cómo cambio mi contraseña?",
        answer:
          "Ve a tu perfil, luego a 'Editar Perfil' y selecciona 'Cambiar Contraseña'. Deberás ingresar tu contraseña actual y luego la nueva contraseña dos veces para confirmar. Recomendamos usar contraseñas seguras con al menos 8 caracteres, incluyendo números, mayusculas y caracteres especiales.",
      },
      {
        question: "¿Mis datos personales están seguros?",
        answer:
          "Sí, el sistema usa encriptación para proteger tu información sensible. Las contraseñas se almacenan con hash seguro, y la comunicación con el servidor está protegida por HTTPS. Solo compartimos tu información con organizadores de torneos en los que participas.",
      },
    ],
  },
]

export default function FAQ() {
  const highlightTimeoutRef = useRef<number | null>(null)
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")

  const toSectionId = (title: string) =>
    `faq-${title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")}`

  const handleSectionJump = (sectionId: string) => {
    const target = document.getElementById(sectionId)

    if (!target) return

    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current)
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    window.history.replaceState(null, "", `#${sectionId}`)
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    })

    setHighlightedSection(sectionId)
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedSection((current) => (current === sectionId ? null : current))
      highlightTimeoutRef.current = null
    }, 3000)
  }

  const sectionSummary = faqCategories.map((category) => ({
    ...category,
    id: toSectionId(category.title),
  }))

  const normalizedSearch = searchTerm.trim().toLowerCase()

  const filteredSectionSummary = sectionSummary
    .map((category) => ({
      ...category,
      questions: category.questions.filter((item) => {
        const normalizedQuestion = item.question.toLowerCase()
        const normalizedAnswer = item.answer.replace(/<[^>]*>/g, " ").toLowerCase()

        return (
          normalizedSearch.length === 0 ||
          normalizedQuestion.includes(normalizedSearch) ||
          normalizedAnswer.includes(normalizedSearch)
        )
      }),
    }))
    .filter((category) => category.questions.length > 0)

  const totalSections = sectionSummary.length
  const totalQuestions = sectionSummary.reduce(
    (acc, category) => acc + category.questions.length,
    0,
  )

  return (
    <Layout>
      <div className="min-h-screen py-4 md:py-6">
        <section className="relative overflow-hidden rounded-2xl border border-primary/15 bg-card/55 px-5 py-10 shadow-[0_10px_45px_rgba(2,6,23,0.08)] backdrop-blur-xl md:px-8 md:py-12">
          <div className="pointer-events-none absolute -left-14 -top-14 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-16 top-10 h-52 w-52 rounded-full bg-cyan-500/10 blur-3xl" />

          <div className="relative mx-auto grid max-w-6xl gap-8 md:grid-cols-2 md:items-center">
            <div className="space-y-4 text-left">
              <h1 className="text-4xl font-black tracking-tight md:text-6xl">
                Preguntas
                <span className="block bg-gradient-to-r from-primary via-cyan-400 to-primary bg-[length:200%_200%] bg-clip-text text-transparent animate-gradient">
                  Frecuentes
                </span>
              </h1>
              <p className="max-w-2xl text-lg text-muted-foreground md:text-xl">
                Encuentra respuestas rápidas sobre torneos, temporadas, cuenta y gestión de tiendas.
              </p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
                <div className="rounded-xl border border-border/70 bg-background/65 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Secciones</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums">{totalSections}</p>
                </div>
                <div className="rounded-xl border border-border/70 bg-background/65 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Preguntas</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums">{totalQuestions}</p>
                </div>
              </div>

              <nav
                aria-label="Navegación de secciones FAQ"
                className="flex flex-wrap justify-start gap-2"
              >
                <AnimatePresence initial={false}>
                  {filteredSectionSummary.map((category) => {
                    const Icon = category.icon
                    return (
                      <motion.a
                        key={category.id}
                        layout
                        href={`#${category.id}`}
                        onClick={(event) => {
                          event.preventDefault()
                          handleSectionJump(category.id)
                        }}
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/65 px-3 py-1.5 text-sm transition-colors hover:border-primary/50 hover:text-primary"
                      >
                        <Icon className="h-3.5 w-3.5 text-primary" />
                        <span className="text-foreground/90">{category.title}</span>
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {category.questions.length}
                        </span>
                      </motion.a>
                    )
                  })}
                </AnimatePresence>
              </nav>

              <div className="space-y-2">
                <label
                  htmlFor="faq-search"
                  className="text-xs uppercase tracking-wide text-muted-foreground"
                >
                  Buscar en preguntas y respuestas
                </label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="faq-search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Ej: swiss, contraseña, tienda..."
                    className="h-10 border-border/70 bg-background/70 pl-9"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="container mx-auto px-4 py-10 md:py-12">
          <div className="mx-auto grid max-w-5xl gap-8">
            {filteredSectionSummary.map((category, categoryIndex) => {
              const Icon = category.icon
              return (
                <Card
                  key={category.id}
                  id={category.id}
                  className={`scroll-mt-28 overflow-hidden border-border/70 bg-card/70 backdrop-blur-sm transition-all duration-500 ${
                    highlightedSection === category.id
                      ? "border-primary/50 shadow-[0_0_0_1px_hsl(var(--primary)/0.35),0_16px_36px_rgba(2,6,23,0.12)]"
                      : ""
                  }`}
                >
                  <CardHeader className="bg-muted/25">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl">{category.title}</CardTitle>
                        <CardDescription className="text-base">
                          {category.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <Accordion type="single" collapsible className="w-full">
                      {category.questions.map((item, questionIndex) => (
                        <AccordionItem
                          key={questionIndex}
                          value={`item-${categoryIndex}-${questionIndex}`}
                          className="border-border/70"
                        >
                          <AccordionTrigger className="text-left hover:no-underline">
                            <span className="font-semibold">{item.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed">
                            <div
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
                            />
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              )
            })}

            {filteredSectionSummary.length === 0 && (
              <Card className="border-border/70 bg-card/70 backdrop-blur-sm">
                <CardContent className="py-10 text-center">
                  <p className="text-lg font-semibold">No encontramos coincidencias</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Prueba con otra palabra clave o limpia el buscador para ver todas las preguntas.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Contact Section */}
          <Card className="mx-auto mt-12 max-w-5xl border-primary/20 bg-gradient-to-r from-primary/15 via-primary/5 to-cyan-500/10 shadow-[0_12px_40px_rgba(2,6,23,0.06)]">
            <CardContent className="pt-8 pb-8">
              <div className="text-center space-y-4">
                <h2 className="text-2xl font-bold">¿No encuentras lo que buscas?</h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Si tienes alguna pregunta que no está respondida aquí, no dudes en contactar al
                  administrador del sistema jo@klu.cl
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-6">
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>Respuestas verificadas</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-primary" />
                    <span>Basado en experiencia de usuarios</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  )
}
